import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LocationLookupService, LocationSelection, locationError, validLocation } from 'src/app/services/location-lookup.service';

@Component({
  selector: 'confirm-google-location',
  templateUrl: './confirm-google-location.component.html',
  styleUrls: ['./confirm-google-location.component.scss'],
})
export class ConfirmGoogleLocationComponent implements AfterViewInit, OnDestroy {
  @Input() location: LocationSelection | null = null;
  @Output() selectionChange = new EventEmitter<LocationSelection | null>();
  @ViewChild('mapContainer') mapContainer!: ElementRef<HTMLElement>;
  busy = true;
  error = '';
  private marker?: google.maps.Marker;
  private map?: google.maps.Map;
  private listeners: google.maps.MapsEventListener[] = [];
  private readonly destroyed$ = new Subject<void>();
  private request?: Subscription;
  private mapRequest?: Subscription;
  private pendingPosition?: google.maps.LatLngLiteral;

  constructor(private lookup: LocationLookupService, private zone: NgZone) {}

  ngAfterViewInit(): void { this.loadMap(); }

  loadMap(): void {
    if (!validLocation(this.location)) {
      this.busy = false; this.error = 'Please choose a location first.'; return;
    }
    this.mapRequest?.unsubscribe();
    this.busy = true; this.error = '';
    this.mapRequest = this.lookup.waitForMaps().pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        const position = { lat: this.location.latitude, lng: this.location.longitude };
        this.map = new google.maps.Map(this.mapContainer.nativeElement, { center: position, zoom: 20, mapTypeControl: false });
        this.marker = new google.maps.Marker({ position, map: this.map, draggable: true,
          icon: {url: '../assets/images/location-select-profile.svg', scaledSize: new google.maps.Size(50, 50)} });
        this.listeners.push(this.marker.addListener('dragstart', () => this.zone.run(() => this.invalidate())));
        this.listeners.push(this.marker.addListener('dragend', () => {
          const point = this.marker.getPosition();
          if (point) this.zone.run(() => this.moveMarker(point.toJSON()));
        }));
        this.listeners.push(this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) this.zone.run(() => this.moveMarker(event.latLng.toJSON()));
        }));
        if (this.location.address) { this.busy = false; this.selectionChange.emit({ ...this.location }); }
        else this.moveMarker(position);
      },
      error: () => { this.busy = false; this.error = 'The map could not load. Please try again.'; }
    });
  }

  moveMarker(position: google.maps.LatLngLiteral): void {
    this.invalidate();
    this.pendingPosition = position;
    this.marker?.setPosition(position);
    this.request = this.lookup.reverse(position).pipe(takeUntil(this.destroyed$)).subscribe({
      next: location => {
        this.location = location; this.busy = false; this.error = '';
        this.selectionChange.emit({ ...location });
      },
      error: error => { this.busy = false; this.error = locationError(error); }
    });
  }

  retry(): void { this.pendingPosition ? this.moveMarker(this.pendingPosition) : this.loadMap(); }

  private invalidate(): void {
    this.request?.unsubscribe(); this.busy = true; this.error = '';
    this.selectionChange.emit(null);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(); this.destroyed$.complete();
    this.listeners.forEach(listener => listener?.remove());
    this.marker?.setMap(null);
  }
}
